/**
 * Colonnes des tables de règles CCQ (schéma 2) — construites à partir de /ccq/meta. Batch #14.298.
 */
const isApprenti = (r) => r.qualification === "apprenti";

export const customTradesFrom = (cfg, meta) => {
  const known = new Set((meta.trades || []).map((t) => t.code));
  const seen = {};
  (cfg.rules?.wage || []).forEach((r) => { if (r.trade_code && !known.has(r.trade_code) && r.trade_code !== "custom") seen[r.trade_code] = r.trade_label || r.trade_code; });
  return Object.entries(seen).map(([code, label]) => ({ code, label }));
};

export const tradeOptions = (meta, cfg) => [...(meta.trades || []), ...customTradesFrom(cfg, meta)];

/** Annexes : codes génériques (standard · léger · lourd) + annexes officielles CCQ importées (C4, R1, N2…) de la configuration. */
export const scheduleOptions = (meta, cfg) => {
  const sectorLabel = Object.fromEntries((meta.sectors || []).map((s) => [s.code, s.label]));
  const official = Object.entries(cfg?.annexes || {}).sort(([a, x], [b, y]) => (x.sector || "").localeCompare(y.sector || "") || (x.rank ?? 0) - (y.rank ?? 0) || a.localeCompare(b))
    .map(([code, a]) => ({ code, label: `${code} — ${a.label}${a.sector ? ` (${sectorLabel[a.sector] || a.sector})` : ""}` }));
  return [...(meta.schedules || []), ...official];
};

export const unionOptions = (meta, cfg) => {
  const base = [...(meta.unions || [])];
  const codes = new Set(base.map((u) => u.code));
  (cfg.rules?.union || []).forEach((r) => { if (r.union_code && !codes.has(r.union_code)) { codes.add(r.union_code); base.push({ code: r.union_code, label: r.label || r.union_code }); } });
  return base;
};

export const premiumOptions = (cfg) => {
  const seen = {};
  (cfg.rules?.premium || []).forEach((r) => { if (r.code) seen[r.code] = r.label || r.code; });
  return Object.entries(seen).map(([code, label]) => ({ code, label }));
};

export const wageColumns = (meta, cfg) => [
  { key: "sector", label: "Secteur", type: "select", options: meta.sectors },
  { key: "schedule", label: "Annexe", type: "select", options: scheduleOptions(meta, cfg), width: "min-w-[170px]" },
  { key: "region", label: "Région", type: "select", options: meta.regions, width: "min-w-[150px]" },
  { key: "trade_code", label: "Métier / occupation", type: "select", options: [...tradeOptions(meta, cfg), { code: "custom", label: "— Autre (libellé ci-contre) —" }], width: "min-w-[180px]" },
  { key: "trade_label", label: "Libellé", type: "text", show: (r) => r.trade_code === "custom", placeholder: "Nom du métier" },
  { key: "qualification", label: "Qualification", type: "select", options: meta.qualifications },
  { key: "period", label: "Période", type: "number", step: "1", width: "w-16", show: isApprenti },
  { key: "hourly_rate", label: "Taux $/h", type: "number", width: "w-24" },
];
export const emptyWage = (cfg) => ({ sector: cfg.sectors?.[0] || "ic", schedule: "standard", region: "", trade_code: "charpentier_menuisier", trade_label: "", qualification: "compagnon", period: "", hourly_rate: "" });

export const contributionColumns = (meta, cfg) => [
  { key: "label", label: "Cotisation", type: "text", placeholder: "Assurance (MÉDIC), retraite, fonds de formation…", width: "min-w-[200px]" },
  { key: "code", label: "Code", type: "text", placeholder: "auto", width: "w-28" },
  { key: "payer", label: "Payée par", type: "select", options: meta.payers, width: "w-28" },
  { key: "basis", label: "Base", type: "select", options: meta.contrib_bases, width: "min-w-[150px]" },
  { key: "rate", label: "Taux", type: "number", step: "0.001", width: "w-24" },
  { key: "recipient", label: "Remise à", type: "select", options: meta.recipients, width: "w-32" },
  { key: "deduction_code", label: "Retenue (bulletin)", type: "select", options: meta.deduction_codes, show: (r) => r.payer === "employee", width: "min-w-[170px]" },
  { key: "scope.sectors", label: "Secteurs visés", type: "multi", options: meta.sectors },
  { key: "scope.trade_codes", label: "Métiers visés", type: "multi", options: tradeOptions(meta, cfg) },
  { key: "scope.qualifications", label: "Qualifications visées", type: "multi", options: meta.qualifications },
  { key: "scope.schedules", label: "Annexes visées", type: "multi", options: scheduleOptions(meta, cfg) },
];
export const emptyContribution = () => ({ label: "", code: "", payer: "employer", basis: "hours", rate: "", recipient: "ccq", deduction_code: "ccq_benefits", scope: { sectors: [], trade_codes: [], qualifications: [], schedules: [] } });

export const unionColumns = (meta, cfg) => [
  { key: "union_code", label: "Association syndicale", type: "select", options: [...meta.unions, { code: "autre", label: "Autre (code libre)" }], width: "min-w-[200px]" },
  { key: "local", label: "Local / section", type: "text", placeholder: "ex. 9", width: "w-24" },
  { key: "trade_code", label: "Métier (facultatif)", type: "select", options: tradeOptions(meta, cfg || {}), blank: "— Tous —", width: "min-w-[160px]" },
  { key: "label", label: "Libellé sur le bulletin", type: "text", placeholder: "Cotisation syndicale", width: "min-w-[170px]" },
  { key: "hourly", label: "$ / h", type: "number", step: "0.001", width: "w-20" },
  { key: "fixed_per_pay", label: "$ fixe / paie", type: "number", width: "w-24" },
  { key: "fixed_per_week", label: "$ / semaine", type: "number", width: "w-24" },
  { key: "fixed_per_month", label: "$ / mois", type: "number", width: "w-24" },
  { key: "pct_wages", label: "% du salaire", type: "number", step: "0.01", width: "w-24" },
];
export const emptyUnion = () => ({ union_code: "ftq", local: "", trade_code: "", label: "", hourly: "", fixed_per_pay: "", fixed_per_week: "", fixed_per_month: "", pct_wages: "" });

export const premiumColumns = (meta) => [
  { key: "label", label: "Prime / fonction", type: "text", placeholder: "Chef d'équipe, hauteur, soir, nuit…", width: "min-w-[190px]" },
  { key: "code", label: "Code", type: "text", placeholder: "auto", width: "w-28" },
  { key: "basis", label: "Base", type: "select", options: meta.premium_bases, width: "w-36" },
  { key: "rate", label: "Montant / %", type: "number", step: "0.001", width: "w-24" },
  { key: "sectors", label: "Secteurs", type: "multi", options: meta.sectors },
  { key: "condition", label: "Condition", type: "text", placeholder: "ex. travaux > 15 m de hauteur", width: "min-w-[170px]" },
  { key: "ccq_wages", label: "Salaire CCQ", type: "bool" },
];
export const emptyPremium = () => ({ label: "", code: "", basis: "per_hour", rate: "", sectors: [], condition: "", ccq_wages: true });

export const overtimeColumns = (meta) => [
  { key: "sector", label: "Secteur", type: "select", options: meta.sectors },
  { key: "daily_after", label: "TS après (h/jour)", type: "number", step: "0.5", width: "w-24" },
  { key: "daily_double_after", label: "Temps double après (h/jour)", type: "number", step: "0.5", width: "w-24" },
  { key: "weekly_after", label: "TS après (h/sem.)", type: "number", step: "0.5", width: "w-24" },
  { key: "saturday_mult", label: "Samedi ×", type: "number", step: "0.5", width: "w-20" },
  { key: "sunday_mult", label: "Dimanche ×", type: "number", step: "0.5", width: "w-20" },
  { key: "holiday_mult", label: "Férié ×", type: "number", step: "0.5", width: "w-20" },
];
export const emptyOvertime = (cfg) => ({ sector: cfg.sectors?.[0] || "ic", daily_after: 8, daily_double_after: "", weekly_after: 40, saturday_mult: 1.5, sunday_mult: 2, holiday_mult: 2 });

export const travelColumns = (meta) => [
  { key: "label", label: "Allocation", type: "text", placeholder: "Déplacement zone 2, chambre et pension…", width: "min-w-[200px]" },
  { key: "sector", label: "Secteur", type: "select", options: meta.sectors, blank: "— Tous —" },
  { key: "region", label: "Région", type: "select", options: meta.regions, width: "min-w-[150px]" },
  { key: "km_from", label: "De (km)", type: "number", step: "1", width: "w-20" },
  { key: "km_to", label: "À (km)", type: "number", step: "1", width: "w-20" },
  { key: "basis", label: "Base", type: "select", options: meta.travel_bases, width: "w-36" },
  { key: "amount", label: "Montant", type: "number", step: "0.001", width: "w-24" },
  { key: "taxable", label: "Imposable", type: "bool" },
];
export const emptyTravel = () => ({ label: "", sector: "", region: "", km_from: "", km_to: "", basis: "per_day", amount: "", taxable: false });

export const RULE_TABS = [
  ["wage", "Taux de salaire"], ["contribution", "Cotisations"], ["union", "Syndicats"], ["premium", "Primes"], ["overtime", "Temps supplémentaire"], ["travel", "Déplacements"],
];
